import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LoginForm,
  ProFormCheckbox,
  ProFormText,
} from "@ant-design/pro-components";
import {
  BankOutlined,
  LockOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { App as AntdApp, theme } from "antd";

import { register } from "./api";
import type { RegisterRequest } from "./types";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { PasswordChecklist } from "@/components/PasswordRules";
import { PASSWORD_PLACEHOLDER, passwordRules } from "@/lib/passwordPolicy";
import { apiErrorMessage } from "@/lib/errors";
import BrandLogo from "@/components/BrandLogo";
import CaptchaWidget from "@/components/CaptchaWidget";
import {
  CAPTCHA_PROMPT,
  useCaptchaConfig,
  useCaptchaToken,
} from "@/lib/captcha";

interface FormValues extends RegisterRequest {
  confirm: string;
  // Collected for the operator's records; the API takes email + password only,
  // so these are not sent until there is somewhere to put them.
  full_name?: string;
  company?: string;
  accepted_terms?: boolean;
}

function RegisterPage() {
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  // Follow the app theme (persisted preference / OS fallback) instead of a
  // hardcoded light backdrop — this page renders before login too.
  const { token } = theme.useToken();
  const setSession = useAuthStore((state) => state.setSession);
  const requestWelcome = useUiStore((state) => state.requestWelcome);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState("");
  // Signup provisions a whole tenant (project, user, network, router, firewall,
  // SSH key) and takes several seconds. A bare spinner reads as a hang, so
  // narrate it.
  const [progress, setProgress] = useState<string | null>(null);
  const captcha = useCaptchaConfig();
  const captchaToken = useCaptchaToken();

  const handleSubmit = async (values: FormValues) => {
    if (values.password !== values.confirm) {
      message.error("Passwords do not match");
      return;
    }
    if (captcha && !captchaToken.token) {
      message.error(CAPTCHA_PROMPT);
      return;
    }
    const usedToken = captchaToken.token;
    setSubmitting(true);
    setProgress("Creating your account…");
    const nextStage = setTimeout(
      () => setProgress("Setting up your private network…"),
      2500,
    );
    try {
      const response = await register({
        email: values.email,
        password: values.password,
        ...(usedToken ? { captcha_token: usedToken } : {}),
      });
      setSession({
        accessToken: response.access_token,
        expiresIn: response.expires_in,
        user: response.user,
      });
      // A toast can't explain what signup just provisioned, and it's gone
      // before anyone reads it — the dashboard shows a welcome instead.
      requestWelcome();
      navigate("/", { replace: true });
    } catch (error) {
      // A CAPTCHA token is single-use.
      if (usedToken) captchaToken.reset();
      message.error(
        apiErrorMessage(error, "Registration failed. Please try again."),
      );
    } finally {
      clearTimeout(nextStage);
      setProgress(null);
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: token.colorBgLayout,
      }}
    >
      <LoginForm<FormValues>
        logo={<BrandLogo />}
        title=""
        subTitle="Create your account"
        onFinish={async (values) => {
          await handleSubmit(values);
          return true;
        }}
        submitter={{
          searchConfig: { submitText: progress ?? "Create account" },
          submitButtonProps: { loading: submitting },
        }}
        actions={<Link to="/login">Already have an account? Sign in</Link>}
      >
        <ProFormText
          name="email"
          fieldProps={{ size: "large", prefix: <MailOutlined /> }}
          placeholder="Email"
          rules={[
            { required: true, message: "Please enter your email" },
            { type: "email", message: "Enter a valid email" },
          ]}
        />
        <ProFormText.Password
          name="password"
          fieldProps={{
            size: "large",
            prefix: <LockOutlined />,
            onChange: (event) => setPassword(event.target.value),
          }}
          placeholder={PASSWORD_PLACEHOLDER}
          rules={passwordRules()}
        />
        <PasswordChecklist value={password} />
        <ProFormText.Password
          name="confirm"
          fieldProps={{ size: "large", prefix: <LockOutlined /> }}
          placeholder="Confirm password"
          rules={[{ required: true, message: "Please confirm your password" }]}
        />
        <ProFormText
          name="full_name"
          fieldProps={{ size: "large", prefix: <UserOutlined /> }}
          placeholder="Full name (optional)"
        />
        <ProFormText
          name="company"
          fieldProps={{ size: "large", prefix: <BankOutlined /> }}
          placeholder="Company (optional)"
        />
        <ProFormCheckbox
          name="accepted_terms"
          rules={[
            {
              validator: (_rule, value: boolean) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Please accept the terms to continue"),
                    ),
            },
          ]}
        >
          I agree to the terms of service and privacy policy
        </ProFormCheckbox>
        {captcha ? (
          <CaptchaWidget
            provider={captcha.provider}
            siteKey={captcha.siteKey}
            onToken={captchaToken.setToken}
            resetKey={captchaToken.resetKey}
          />
        ) : null}
      </LoginForm>
    </div>
  );
}

export default RegisterPage;
